import { useState } from "react";
import clsx from "clsx";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendarCheck,
  faLock,
  faReceipt,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import CustomButton from "../customs/custom-button";
import {
  cashTotal,
  instalmentRows,
  money,
  policyRows,
  PublicChase,
  quoteRows,
  Row,
} from "./payment-page.constants";

interface IFullPaymentDetails {
  chase: PublicChase;
  loading: boolean;
  onProceed: () => void;
}

/**
 * One line of a breakdown. A `sub` row is money charged ON the line above — IPT
 * on the premium, interest on the price — so it sits indented under an elbow
 * rather than reading as another item in the list.
 */
const DetailRow = ({ label, value, sub, credit }: Row) => (
  <div
    className={clsx(
      "flex items-center justify-between gap-4 py-[9px] text-sm",
      sub && "pl-3.5"
    )}
  >
    <p
      className={clsx(
        "flex items-center gap-2 min-w-0",
        sub ? "text-light-gray text-[13px]" : "text-text-secondary"
      )}
    >
      {sub ? (
        <span className="w-2 h-2 shrink-0 -mt-2 border-l border-b border-edge-strong rounded-bl-[3px]" />
      ) : null}
      <span className="truncate">{label}</span>
    </p>
    <p
      className={clsx(
        "font-semibold text-right tabular-nums shrink-0",
        credit
          ? "text-emerald-500"
          : sub
            ? "text-text-secondary text-[13px]"
            : "text-text-primary"
      )}
    >
      {value}
    </p>
  </div>
);

/** A titled card of rows, optionally closing on the figure they add up to. */
const Section = ({
  icon,
  title,
  rows,
  total,
}: {
  icon: IconProp;
  title: string;
  rows: Row[];
  total?: Row;
}) =>
  rows.length ? (
    <div className="rounded-2xl border border-edge bg-surface-muted overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-edge-faint">
        <FontAwesomeIcon
          icon={icon}
          className="text-[11px]"
          style={{ color: "var(--brand)" }}
        />
        <p className="text-label-muted text-[11px] font-semibold uppercase tracking-[1.2px]">
          {title}
        </p>
      </div>

      <div className="px-4 py-1 divide-y divide-edge-faint">
        {rows.map((row) => (
          <DetailRow key={row.label} {...row} />
        ))}
      </div>

      {total ? (
        <div
          className="flex items-center justify-between gap-4 px-4 py-3.5 border-t border-edge"
          style={{ background: "var(--brand-soft)" }}
        >
          <p className="text-heading-color font-bold text-sm">{total.label}</p>
          <p
            className="text-[19px] font-bold tabular-nums leading-none"
            style={{ color: "var(--brand)" }}
          >
            {total.value}
          </p>
        </div>
      ) : null}
    </div>
  ) : null;

const FullPaymentDetails = ({
  chase,
  loading,
  onProceed,
}: IFullPaymentDetails) => {
  const isCash = chase.paymentType === "cash";
  const [value, setValue] = useState({ confirm: false });
  const set = (patch: Partial<typeof value>) =>
    setValue((s) => ({ ...s, ...patch }));

  return (
    <div className="flex flex-col gap-3.5">
      {/* ---------------------------- AMOUNT DUE ----------------------------
          The one number the customer came for, in the brand's own colour. */}
      <div
        className="relative overflow-hidden rounded-2xl border px-5 py-6 text-center"
        style={{
          background: "var(--brand-soft)",
          borderColor: "var(--brand-edge)",
        }}
      >
        {/* Soft brand bloom — depth without a second surface. */}
        <span
          aria-hidden
          className="absolute -top-16 -right-10 w-40 h-40 rounded-full blur-3xl opacity-40 pointer-events-none"
          style={{ background: "var(--brand)" }}
        />
        <div className="relative">
          <p className="text-label-muted text-[11px] font-semibold uppercase tracking-[1.2px]">
            {isCash ? "Amount due today" : "Monthly payment"}
          </p>
          <p className="text-heading-color text-[40px] font-bold leading-[52px] tabular-nums">
            {money(isCash ? cashTotal(chase) : chase.monthlyAmount)}
            {isCash ? null : (
              <span className="text-text-secondary text-[17px] font-semibold">
                {" "}
                /mo
              </span>
            )}
          </p>
          <p className="text-text-secondary text-[13px]">
            {isCash
              ? "One-off payment for your renewal"
              : `${chase.instalments || 0} monthly instalments — ${money(
                  chase.totalPayable ?? chase.premium
                )} total payable`}
          </p>

          {chase.renewalDate ? (
            <span
              className="inline-flex items-center gap-2 mt-3.5 rounded-full border bg-surface-elevated px-3 py-1.5 text-[12px] font-semibold text-text-primary"
              style={{ borderColor: "var(--brand-edge)" }}
            >
              <FontAwesomeIcon
                icon={faCalendarCheck}
                className="text-[11px]"
                style={{ color: "var(--brand)" }}
              />
              Renews {moment(chase.renewalDate).format("D MMM YYYY")}
            </span>
          ) : null}
        </div>
      </div>

      <Section
        icon={faShieldHalved}
        title="Your policy"
        rows={policyRows(chase)}
      />

      {/* The quote itself — shown either way, so IPT is visible on both. A cash
          customer's total closes it off; an instalment customer's plan follows. */}
      <Section
        icon={faReceipt}
        title="Payment breakdown"
        rows={quoteRows(chase)}
        total={
          isCash
            ? { label: "Total due", value: money(cashTotal(chase)) }
            : undefined
        }
      />

      {isCash ? null : (
        <Section
          icon={faCalendarCheck}
          title="Instalment plan"
          rows={instalmentRows(chase)}
          total={{
            label: "Monthly payment",
            value: money(chase.monthlyAmount),
          }}
        />
      )}

      {/* ---------------------------- PROCEED ---------------------------- */}
      {/* Instalments take no card — the customer only confirms their existing plan. */}
      {value.confirm ? (
        <div className="rounded-2xl border border-edge bg-surface-muted px-4 py-4 flex flex-col gap-3 animate-fade-in">
          <p className="text-text-secondary text-sm text-center">
            Your previous payment details will be used to renew your policy. Do
            you confirm?
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="w-1/3 h-[50px] rounded-lg border border-edge text-text-secondary text-sm font-medium hover:bg-overlay transition-colors duration-200 disabled:opacity-50"
              onClick={() => set({ confirm: false })}
              disabled={loading}
            >
              Cancel
            </button>
            <CustomButton
              variant="primary"
              fullWidth
              loading={loading}
              onClick={onProceed}
            >
              Confirm
            </CustomButton>
          </div>
        </div>
      ) : (
        <CustomButton
          variant="primary"
          fullWidth
          loading={loading}
          onClick={() => (isCash ? onProceed() : set({ confirm: true }))}
          className="!h-[52px] !text-[15px] mt-0.5"
        >
          {isCash ? "Proceed to Pay" : "Pay by Direct Debit"}
        </CustomButton>
      )}

      <p className="flex items-center justify-center gap-2 text-text-secondary text-[12px]">
        <FontAwesomeIcon icon={faLock} className="text-[10px]" />
        {isCash
          ? "Card details are handled securely by Stripe — we never see them."
          : "Nothing is collected today — this only confirms your existing plan."}
      </p>
    </div>
  );
};

export default FullPaymentDetails;
